import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";
import V2MuscleDetailModal from "./V2MuscleDetailModal";
import V2PriorityPlayerCard from "./V2PriorityPlayerCard";
import { V2Skeleton } from "./V2BusyFrame";
import useV2AvatarModel from "./useV2AvatarModel";
import { normalizeMuscleSelection } from "./v2PriorityUtils";

const SORT_OPTIONS = [
  { id: "criticality", labelKey: "v2.overview.sort_criticality" },
  { id: "name", labelKey: "v2.overview.sort_name" },
  { id: "position", labelKey: "v2.overview.sort_position" },
];

export default function V2PrioritySection({ players, clubId, date, period, loading = false }) {
  const { t } = useTranslation();
  const [sortBy, setSortBy] = useState("criticality");
  const [sortOpen, setSortOpen] = useState(false);
  const [muscleDetail, setMuscleDetail] = useState(null);
  const [selectionResetToken, setSelectionResetToken] = useState(0);
  const sortRef = useRef(null);
  const { gltf, status } = useV2AvatarModel();

  const closeMuscle = useCallback(() => {
    setMuscleDetail(null);
    setSelectionResetToken((value) => value + 1);
  }, []);

  const priority = useMemo(() => {
    const list = players.filter((player) => player.zone === "overload");
    if (sortBy === "name") return [...list].sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "position") return [...list].sort((a, b) => a.position.localeCompare(b.position) || a.score - b.score);
    return [...list].sort((a, b) => (a.criticality || 99) - (b.criticality || 99) || a.score - b.score);
  }, [players, sortBy]);

  const sortLabel = t(SORT_OPTIONS.find((option) => option.id === sortBy)?.labelKey || SORT_OPTIONS[0].labelKey);

  useEffect(() => {
    if (!sortOpen) return undefined;
    const onPointerDown = (event) => {
      if (!sortRef.current?.contains(event.target)) setSortOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setSortOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [sortOpen]);

  const openMuscle = (player, muscle, pointer) => {
    const detail = normalizeMuscleSelection(muscle, player);
    if (!detail) return;
    setMuscleDetail({
      ...detail,
      playerId: player.id,
      x: pointer?.x ?? window.innerWidth / 2,
      y: pointer?.y ?? window.innerHeight / 2,
    });
  };

  return (
    <section className="mt-[23px]">
      <div className="mb-3 mt-2 flex items-center justify-between gap-2.5">
        <div className="flex items-center gap-2.5">
          <h3 className="m-0 text-[15px] text-[#f4f2ff]">{t("v2.overview.priority_title")}</h3>
          <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-[14px] border border-[rgba(255,77,94,0.7)] bg-[rgba(255,77,94,0.1)] px-2 py-1 text-[11px] font-semibold text-[#ff6e7d]">
            {t("v2.overview.players_count", { count: priority.length })}
          </span>
        </div>
        {priority.length > 0 && (
          <div className="relative flex items-center gap-1.5 text-[12px] text-[#aaa5d7]" ref={sortRef}>
            <span>{t("v2.overview.sort")}</span>
            <button
              type="button"
              className={`inline-flex min-h-[32px] items-center gap-1.5 rounded-[5px] border bg-[#110c3d] px-2.5 text-[12px] text-[#f1efff] ${
                sortOpen
                  ? "border-[#34cbff] shadow-[0_0_0_2px_rgba(52,203,255,0.12)]"
                  : "border-[rgba(98,94,166,0.55)] hover:border-[#34cbff]"
              }`}
              onClick={() => setSortOpen((open) => !open)}
              aria-haspopup="listbox"
              aria-expanded={sortOpen}
            >
              {sortLabel}
              <ChevronDown size={14} className={`text-[#9b96c9] transition-transform ${sortOpen ? "rotate-180" : ""}`} />
            </button>
            {sortOpen && (
              <ul
                className="absolute right-0 top-[calc(100%+6px)] z-30 m-0 min-w-[168px] list-none overflow-hidden rounded-lg border border-[rgba(98,94,166,0.55)] bg-[#19144e] p-1 shadow-[0_16px_40px_rgba(2,0,16,0.55)]"
                role="listbox"
              >
                {SORT_OPTIONS.map((option) => {
                  const active = option.id === sortBy;
                  return (
                    <li key={option.id}>
                      <button
                        type="button"
                        className={`flex w-full items-center justify-between gap-2 rounded-md border-0 px-2.5 py-1.5 text-left text-[12px] ${
                          active
                            ? "bg-[rgba(52,203,255,0.12)] text-[#34cbff]"
                            : "bg-transparent text-[#f1efff] hover:bg-[rgba(98,94,166,0.2)]"
                        }`}
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          setSortBy(option.id);
                          setSortOpen(false);
                        }}
                      >
                        {t(option.labelKey)}
                        {active && <Check size={13} />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {loading && priority.length === 0 ? (
        <div className="grid grid-cols-4 gap-3.5 max-[1480px]:grid-cols-2 max-[860px]:grid-cols-1" aria-hidden="true">
          {Array.from({ length: 2 }, (_, index) => (
            <div
              key={index}
              className="min-h-[220px] rounded-xl border border-[rgba(98,94,166,0.35)] bg-[rgba(11,7,53,0.45)] p-4"
            >
              <div className="mb-4 flex items-center gap-2.5">
                <V2Skeleton className="h-8 w-8 rounded-full" />
                <div className="min-w-0 flex-1">
                  <V2Skeleton className="h-3 w-28" />
                  <V2Skeleton className="mt-2 h-2 w-16" />
                </div>
              </div>
              <V2Skeleton className="h-[140px] w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : priority.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[rgba(98,94,166,0.45)] p-7 text-center">
          <strong>{t("v2.overview.priority.empty_title")}</strong>
          <p className="mt-2 text-[#8f8abf]">{t("v2.overview.priority.empty_desc")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3.5 max-[1480px]:grid-cols-2 max-[860px]:grid-cols-1">
          {priority.map((player) => (
            <V2PriorityPlayerCard
              key={player.id}
              player={player}
              clubId={clubId}
              date={date}
              period={period}
              gltf={gltf}
              avatarStatus={status}
              onSelectMuscle={openMuscle}
              onClearMuscle={closeMuscle}
              selectionResetToken={selectionResetToken}
              onViewPlayer={() => {}}
            />
          ))}
        </div>
      )}

      <V2MuscleDetailModal detail={muscleDetail} onClose={closeMuscle} />
    </section>
  );
}
